import { useState, useEffect, useRef } from 'react';
import { money, originBase } from '../util';

// Columnas de la tabla de cajas. La última es `auto` y contiene TODOS los botones,
// para que crezca con ellos en vez de que se pisen.
const COLS = '1.3fr 0.6fr 1fr 0.9fr auto';

export default function TabCajas({ event, api }) {
  const [boxes, setBoxes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [lots, setLots] = useState([]);
  const [sales, setSales] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: '', pin: '', menu_id: '', boleteria_lot_ids: [], hanger_from: 1, hanger_to: 50, kind: 'caja', sector_ids: [] });
  const [sectors, setSectors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resumen, setResumen] = useState(null);   // caja cuyo resumen de ventas se ve
  const [sub, setSub] = useState('cajas');        // 'cajas' | 'articulos'
  const timer = useRef(null);

  const loadBoxes = () => api.get(`/api/tpv/boxes?event_id=${event.id}`).then(setBoxes).catch(() => {});
  useEffect(() => {
    loadBoxes();
    api.get('/api/tpv/menus').then(setMenus).catch(() => {});
    api.get(`/api/events/${event.id}/lots`).then(setLots).catch(() => {});
    api.get(`/api/mesas/sectors?event_id=${event.id}`).then(setSectors).catch(() => {});
  }, []); // eslint-disable-line

  // vendido en vivo
  useEffect(() => {
    // Vendido por caja agrupado en la base (una fila por caja), en vez de bajar
    // todas las ventas del evento cada 4s para sumarlas acá.
    const tick = () => api.get(`/api/tpv/sales/by-box?event_id=${event.id}`).then(setSales).catch(() => {});
    tick();
    timer.current = setInterval(tick, 4000);
    return () => clearInterval(timer.current);
  }, []); // eslint-disable-line

  const soldByBox = (boxId) => Number(sales.find(s => s.box_id === boxId)?.total || 0);
  // Ventas cobradas que todavía no llegaron al servidor (viven en la tablet).
  const pendByBox = (boxId) => Number(sales.find(s => s.box_id === boxId)?.pending || 0);
  const beatByBox = (boxId) => sales.find(s => s.box_id === boxId)?.last_beat || null;
  const pendTotal = sales.reduce((a, s) => a + (Number(s.pending) || 0), 0);
  const cajasConPend = sales.filter(s => Number(s.pending) > 0);
  const hace = (iso) => {
    if (!iso) return null;
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 2) return 'ahora';
    if (min < 60) return `hace ${min} min`;
    return `hace ${Math.floor(min / 60)} h`;
  };
  const menuTypeOf = (id) => menus.find(m => m.id === id)?.type || 'barra';
  const boleteriaLots = lots.filter(l => Array.isArray(l.channels) && l.channels.includes('boleteria'));
  const isBoleteria = menuTypeOf(f.menu_id) === 'boleteria';
  const isGuardarropa = menuTypeOf(f.menu_id) === 'guardarropa';
  const isMoza = f.kind === 'moza';

  async function create() {
    if (!f.name.trim() || f.pin.length !== 4) { alert('Nombre y PIN de 4 dígitos requeridos'); return; }
    // Una moza sin sectores no recibiría ningún pedido: mejor frenarlo acá que dejarla
    // toda la noche mirando una pantalla vacía.
    if (isMoza && !f.sector_ids.length) { alert('Elegí al menos un sector: si no, no le llega ningún pedido.'); return; }
    try {
      await api.post('/api/tpv/boxes', { event_id: event.id, name: f.name, pin: f.pin, kind: f.kind,
        menu_id: isMoza ? null : (f.menu_id || null),
        sector_ids: isMoza ? f.sector_ids : [],
        boleteria_lot_ids: isBoleteria ? f.boleteria_lot_ids : [], ...(isGuardarropa ? { hanger_from: f.hanger_from, hanger_to: f.hanger_to } : {}) });
      setF({ name: '', pin: '', menu_id: '', boleteria_lot_ids: [], hanger_from: 1, hanger_to: 50, kind: 'caja', sector_ids: [] }); setShowForm(false); loadBoxes();
    } catch (e) { alert(e.message); }
  }

  if (sub === 'articulos') {
    return (
      <div className="lb-fade" style={{ paddingTop: 30 }}>
        <SubTabs sub={sub} setSub={setSub} />
        <VentaArticulos event={event} api={api} />
      </div>
    );
  }

  return (
    <div className="lb-fade" style={{ paddingTop: 30 }}>
      <SubTabs sub={sub} setSub={setSub} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div className="lb-eyebrow">Cajas TPV del evento</div>
        <button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancelar' : '+ Nueva caja'}</button>
      </div>

      {showForm && (
        <div className="lb-card" style={{ padding: '20px 24px', marginBottom: 16 }}>
          {/* Caja o moza: cambia qué se pide abajo (menú vs sectores) y por dónde entra
              (el TPV es la app; la moza entra por /mozas desde su teléfono). */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[['caja', 'Caja (barra, boletería, guardarropa)'], ['moza', 'Moza (servicio a la mesa)']].map(([k, t]) => (
              <button key={k} className={`lb-btn lb-btn--sm ${f.kind === k ? 'lb-btn--white' : 'lb-btn--ghost'}`}
                onClick={() => setF({ ...f, kind: k })}>{t}</button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.7fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <Field label="Nombre"><input className="lb-input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder={isMoza ? 'Ana' : 'BOLETERÍA · Puerta'} /></Field>
            <Field label="PIN (4 díg.)"><input className="lb-input" value={f.pin} inputMode="numeric" maxLength={4} onChange={e => setF({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="1111" /></Field>
            {isMoza ? (
              <Field label="Sectores que atiende">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {sectors.map(s => {
                    const on = f.sector_ids.includes(s.id);
                    return (
                      <button key={s.id} className={`lb-btn lb-btn--sm ${on ? 'lb-btn--white' : 'lb-btn--ghost'}`}
                        onClick={() => setF({ ...f, sector_ids: on ? f.sector_ids.filter(x => x !== s.id) : [...f.sector_ids, s.id] })}>
                        {s.name}
                      </button>
                    );
                  })}
                  {!sectors.length && <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>Cargá sectores en Mesas primero.</span>}
                </div>
              </Field>
            ) : (
              <Field label="Menú que vende">
                <select className="lb-select" value={f.menu_id} onChange={e => setF({ ...f, menu_id: e.target.value })}>
                  <option value="">— Sin menú —</option>
                  {menus.map(m => <option key={m.id} value={m.id}>{m.name}{m.type === 'boleteria' ? ' · Boletería' : m.type === 'guardarropa' ? ' · Guardarropa' : ''}</option>)}
                </select>
              </Field>
            )}
            <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={create}>Crear</button>
          </div>
          {isMoza && (
            <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 10, lineHeight: 1.5 }}>
              Varias mozas pueden compartir un sector: todas ven los pedidos y se lo lleva la que lo toma primero.
              Entra desde su teléfono a <b style={{ color: '#fff' }}>{originBase()}/mozas</b> con este PIN.
              Sirve el menú de la barra del sector.
            </div>
          )}
          {isBoleteria && (
            <LotPicker lots={boleteriaLots} selected={f.boleteria_lot_ids} onChange={ids => setF({ ...f, boleteria_lot_ids: ids })} />
          )}
          {isGuardarropa && (
            <HangerRange from={f.hanger_from} to={f.hanger_to} onChange={(a, b) => setF({ ...f, hanger_from: a, hanger_to: b })} />
          )}
        </div>
      )}

      {/* AVISO DE VENTAS SIN SINCRONIZAR. Va arriba y en rojo porque es plata cobrada que
          todavía no llegó al servidor: mientras siga en la tablet, un borrado de datos
          del navegador o una reinstalación se la lleva. Y es accionable EN LA NOCHE —
          casi siempre es la red de ese sector. */}
      {pendTotal > 0 && (
        <div className="lb-card" style={{ padding: '14px 18px', marginBottom: 14, border: '1px solid rgba(200,16,46,0.6)', background: 'rgba(200,16,46,0.10)' }}>
          <div style={{ color: 'var(--red-400)', fontWeight: 700, fontSize: 13 }}>
            ⚠ {pendTotal} {pendTotal === 1 ? 'venta cobrada sin sincronizar' : 'ventas cobradas sin sincronizar'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 4, lineHeight: 1.5 }}>
            {cajasConPend.map(s => {
              const b = boxes.find(x => x.id === s.box_id);
              return `${b ? b.name : 'caja desconocida'}: ${s.pending}`;
            }).join(' · ')}
            {'. '}Esa plata está guardada en la tablet y sube sola cuando recupera conexión, pero mientras tanto <b style={{ color: '#fff' }}>no está en el servidor</b>: revisá la red de ese sector y no borres los datos del navegador ni reinstales la app en esa tablet.
          </div>
        </div>
      )}

      <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
        {/* Los tres botones van en UNA sola celda: antes cada uno tenía su columna de
            grid y el contenido era más ancho que la columna, así que se superponían. */}
        <div className="lb-row lb-th" style={{ gridTemplateColumns: COLS }}>
          <span>Caja</span><span>PIN</span><span>Menú</span><span>Vendido</span><span></span>
        </div>
        {boxes.map(b => (
          <div key={b.id} className="lb-row" style={{ gridTemplateColumns: COLS, alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', color: '#fff' }}>{b.name}</span>
            <span style={{ fontFamily: 'monospace', color: 'var(--ink-800)', letterSpacing: '0.2em' }}>{b.pin}</span>
            <span style={{ color: 'var(--ink-700)', fontSize: 13 }}>
              {b.kind === 'moza'
                ? `🛎️ ${(Array.isArray(b.sector_ids) ? b.sector_ids : []).map(id => sectors.find(s => s.id === id)?.name).filter(Boolean).join(', ') || 'sin sector'}`
                : `${b.menu_name || '—'}${b.menu_type === 'boleteria' ? ' 🎟️' : ''}`}
            </span>
            <span style={{ color: '#fff', fontWeight: 600, fontFamily: 'var(--font-display)' }}>
              {money(soldByBox(b.id))}
              {/* Lo pendiente va DEBAJO del vendido y no sumado: son cosas distintas —
                  una está cobrada y registrada, la otra cobrada y todavía no. */}
              {pendByBox(b.id) > 0 && (
                <div style={{ fontSize: 10.5, color: 'var(--red-400)', fontWeight: 700, fontFamily: 'var(--font-body)', marginTop: 2 }}>
                  +{pendByBox(b.id)} sin sincronizar
                  {beatByBox(b.id) && <span style={{ color: 'var(--ink-600)', fontWeight: 400 }}> · {hace(beatByBox(b.id))}</span>}
                </div>
              )}
            </span>
            <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="lb-btn lb-btn--white lb-btn--sm" style={{ whiteSpace: 'nowrap' }} onClick={() => setResumen(b)}>Ver resumen</button>
              <a className="lb-btn lb-btn--ghost lb-btn--sm" style={{ whiteSpace: 'nowrap' }} href={`${originBase()}${b.kind === 'moza' ? '/mozas' : '/tpv'}`} target="_blank" rel="noreferrer">{b.kind === 'moza' ? 'Abrir MOZAS ↗' : 'Abrir TPV ↗'}</a>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ whiteSpace: 'nowrap' }} onClick={() => setEditing(b)}>Editar</button>
            </span>
          </div>
        ))}
        {boxes.length === 0 && <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--ink-600)' }}>Sin cajas. Creá la primera.</div>}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-600)' }}>El monto vendido se actualiza en vivo. Quien atiende la caja entra a /tpv con el PIN.</div>

      {resumen && <ResumenVentaModal box={resumen} event={event} menus={menus} api={api} onClose={() => setResumen(null)} />}
      {editing && <EditBoxModal box={editing} menus={menus} boleteriaLots={boleteriaLots} api={api} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadBoxes(); }} />}
    </div>
  );
}

function SubTabs({ sub, setSub }) {
  return (
    <div className="lb-subtabs" style={{ marginBottom: 20 }}>
      <button className={sub === 'cajas' ? 'active' : ''} onClick={() => setSub('cajas')}>Cajas</button>
      <button className={sub === 'articulos' ? 'active' : ''} onClick={() => setSub('articulos')}>Venta de artículos</button>
    </div>
  );
}

// Qué vendió cada caja, artículo por artículo, y el total de la noche.
//
// El agrupado lo hace la base (/sales/articles): los ítems están en un JSONB por
// venta, y bajarlas todas para sumarlas acá es lo que ya habíamos sacado del polling.
// Las ventas ANULADAS no cuentan, igual que en el dinero de la caja.
function VentaArticulos({ event, api }) {
  const [data, setData] = useState(null);
  const [abierta, setAbierta] = useState(null);   // caja desplegada
  const [cargando, setCargando] = useState(false);
  const timer = useRef(null);

  const refrescar = () => {
    setCargando(true);
    return api.get(`/api/tpv/sales/articles?event_id=${event.id}`)
      .then(setData)
      .catch(() => setData(d => d || { articulos: [], total: 0, unidades: 0 }))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    const tick = () => api.get(`/api/tpv/sales/articles?event_id=${event.id}`).then(setData).catch(() => setData(d => d || { articulos: [], total: 0, unidades: 0 }));
    tick();
    // 10 minutos, no segundos: de noche la prioridad es el TPV. Este resumen es para
    // mirar cómo viene la barra, no un tablero en vivo — y cada consulta desarma el
    // JSONB de todas las ventas del evento, que es lo mas caro de esta pantalla.
    timer.current = setInterval(tick, 10 * 60 * 1000);
    return () => clearInterval(timer.current);
  }, []); // eslint-disable-line

  if (!data) return <div style={{ padding: '30px 0', color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>;

  // Por caja, y dentro de cada caja sus artículos de mayor a menor.
  const porCaja = {};
  for (const a of data.articulos) {
    porCaja[a.box_id] = porCaja[a.box_id] || { name: a.box_name, items: [], total: 0, unidades: 0 };
    porCaja[a.box_id].items.push(a);
    porCaja[a.box_id].total += a.monto;
    porCaja[a.box_id].unidades += a.cantidad;
  }
  const cajas = Object.entries(porCaja).sort((a, b) => b[1].total - a[1].total);

  // El mismo artículo sumado de TODAS las cajas: el ranking real de la noche.
  const global = {};
  for (const a of data.articulos) {
    global[a.articulo] = global[a.articulo] || { articulo: a.articulo, cantidad: 0, monto: 0 };
    global[a.articulo].cantidad += a.cantidad;
    global[a.articulo].monto += a.monto;
  }
  const ranking = Object.values(global).sort((a, b) => b.monto - a.monto);

  if (!data.articulos.length) {
    return (
      <div className="lb-card" style={{ padding: '30px 24px', textAlign: 'center', color: 'var(--ink-600)', fontSize: 13 }}>
        Todavía no hay artículos vendidos en este evento.
      </div>
    );
  }

  return (
    <div>
      <div className="tk-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 18, marginBottom: 22 }}>
        <div className="lb-card" style={{ padding: '24px 26px' }}>
          <div className="lb-eyebrow">Venta total de artículos</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 28, color: 'var(--red-400)', marginTop: 6 }}>{money(data.total)}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 4 }}>{data.unidades} unidades · {cajas.length} {cajas.length === 1 ? 'caja' : 'cajas'}</div>
        </div>
        <div className="lb-card" style={{ padding: '24px 26px' }}>
          <div className="lb-eyebrow">Más vendido</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: '#fff', marginTop: 6, textTransform: 'uppercase' }}>{ranking[0]?.articulo}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 4 }}>{ranking[0]?.cantidad} unidades · {money(ranking[0]?.monto || 0)}</div>
        </div>
      </div>

      {/* Con 10 minutos de refresco hace falta poder pedirlo a mano: si no, mirar cómo
          viene la barra ahora mismo obligaría a esperar o a recargar toda la página. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
        <div className="lb-eyebrow">Total por artículo · todas las cajas</div>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={refrescar} disabled={cargando}>
          {cargando ? 'Actualizando…' : 'Actualizar ahora'}
        </button>
      </div>
      <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px', marginBottom: 26 }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: '2fr 0.8fr 1fr' }}>
          <span>Artículo</span><span>Cantidad</span><span>Monto</span>
        </div>
        {ranking.map(a => (
          <div key={a.articulo} className="lb-row" style={{ gridTemplateColumns: '2fr 0.8fr 1fr' }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{a.articulo}</span>
            <span style={{ color: 'var(--ink-800)' }}>{a.cantidad}</span>
            <span style={{ color: '#fff', fontWeight: 600 }}>{money(a.monto)}</span>
          </div>
        ))}
        <div className="lb-row" style={{ gridTemplateColumns: '2fr 0.8fr 1fr', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
          <span style={{ color: 'var(--ink-700)', fontSize: 12 }}>Total</span>
          <span style={{ color: '#fff', fontWeight: 700 }}>{data.unidades}</span>
          <span style={{ color: '#fff', fontWeight: 700 }}>{money(data.total)}</span>
        </div>
      </div>

      <div className="lb-eyebrow" style={{ marginBottom: 10 }}>Por caja</div>
      {cajas.map(([boxId, c]) => {
        const open = abierta === boxId;
        return (
          <div key={boxId} className="lb-card" style={{ padding: '6px 24px', marginBottom: 12 }}>
            <div className="lb-row" style={{ gridTemplateColumns: '2fr 0.8fr 1fr auto', borderBottom: open ? '1px solid rgba(255,255,255,0.07)' : 0, alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setAbierta(open ? null : boxId)}>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', color: '#fff' }}>{c.name}</span>
              <span style={{ color: 'var(--ink-800)' }}>{c.unidades} u.</span>
              <span style={{ color: '#fff', fontWeight: 600 }}>{money(c.total)}</span>
              <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: open ? '#fff' : 'var(--ink-700)', textAlign: 'right' }}>{open ? 'Ocultar ▾' : 'Ver artículos ▸'}</span>
            </div>
            {open && (
              <div style={{ padding: '4px 0 14px' }}>
                {c.items.map(a => (
                  <div key={a.articulo} className="lb-row" style={{ gridTemplateColumns: '2fr 0.8fr 1fr', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ color: 'var(--ink-800)', fontSize: 13 }}>{a.articulo}</span>
                    <span style={{ color: 'var(--ink-800)' }}>{a.cantidad}</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{money(a.monto)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-600)' }}>Se actualiza cada 10 minutos (de noche la prioridad es el TPV) — o al volver a entrar a la pestaña. No incluye boletería, que vende entradas y no artículos. Las ventas anuladas no se cuentan, igual que en el dinero de la caja.</div>
    </div>
  );
}

// Rango de perchas del guardarropa. La numeración es POR CAJA (índice único por box_id):
// dos puestos pueden ir los dos del 1 al 200 y el ticket sale con PUESTO + PERCHA. Sigue
// siendo un rango por si alguien prefiere numerar corrido (1-60 y 61-120).
function HangerRange({ from = 1, to = 50, onChange }) {
  const total = Math.max(0, (Number(to) || 0) - (Number(from) || 0) + 1);
  return (
    <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
      <div className="lb-label" style={{ marginBottom: 4 }}>Perchas de esta caja</div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 10 }}>La caja va a mostrar una grilla con estos números. Cada caja numera sus perchas por separado: si tenés <b>dos guardarropas</b>, los dos pueden ir del 1 al 200, porque el ticket sale con el nombre de la caja y el número.</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
        <Field label="Desde"><input className="lb-input" style={{ width: 90 }} type="number" min={1} value={from} onChange={e => onChange(Math.max(1, parseInt(e.target.value, 10) || 1), to)} /></Field>
        <Field label="Hasta"><input className="lb-input" style={{ width: 90 }} type="number" min={1} value={to} onChange={e => onChange(from, Math.max(1, parseInt(e.target.value, 10) || 1))} /></Field>
        <div style={{ fontSize: 13, color: total > 0 ? '#fff' : 'var(--red-400)', paddingBottom: 10 }}>{total > 0 ? total + ' perchas' : 'rango inválido'}</div>
      </div>
    </div>
  );
}

// Selector de lotes para una caja de boletería (qué lotes vende esa caja).
function LotPicker({ lots, selected, onChange }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  return (
    <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
      <div className="lb-label" style={{ marginBottom: 4 }}>Lotes que vende esta boletería</div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 10 }}>Si no elegís ninguno, vende <b>todos</b> los lotes de boletería del evento. Elegí solo si querés que esta caja venda un subconjunto.</div>
      {lots.length === 0
        ? <div style={{ fontSize: 12.5, color: 'var(--ink-600)' }}>Este evento no tiene lotes con canal <b>Boletería</b> todavía. Aplicá un grupo que los tenga, o marcá "Boletería" como medio de venta en los lotes (pestaña Productos).</div>
        : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {lots.map(l => {
              const on = selected.includes(l.id);
              return (
                <button key={l.id} type="button" onClick={() => toggle(l.id)}
                  className={'lb-btn lb-btn--sm ' + (on ? 'lb-btn--white' : 'lb-btn--ghost')}>
                  {on ? '✓ ' : ''}{l.name} · {money(l.price)}
                </button>
              );
            })}
          </div>
        )}
    </div>
  );
}

function EditBoxModal({ box, menus, boleteriaLots, api, onClose, onSaved }) {
  const [f, setF] = useState({ name: box.name || '', pin: box.pin || '', menu_id: box.menu_id || '', boleteria_lot_ids: Array.isArray(box.boleteria_lot_ids) ? box.boleteria_lot_ids : [], hanger_from: box.hanger_from ?? 1, hanger_to: box.hanger_to ?? 50 });
  const [saving, setSaving] = useState(false);
  const isBoleteria = (menus.find(m => m.id === f.menu_id)?.type || 'barra') === 'boleteria';
  const isGuardarropa = (menus.find(m => m.id === f.menu_id)?.type || 'barra') === 'guardarropa';
  async function save() {
    if (!f.name.trim() || f.pin.length !== 4) { alert('Nombre y PIN de 4 dígitos requeridos'); return; }
    setSaving(true);
    try { await api.patch(`/api/tpv/boxes/${box.id}`, { name: f.name, pin: f.pin, menu_id: f.menu_id || null, boleteria_lot_ids: isBoleteria ? f.boleteria_lot_ids : [], ...(isGuardarropa ? { hanger_from: f.hanger_from, hanger_to: f.hanger_to } : {}) }); onSaved(); }
    catch (e) { alert(e.message); } finally { setSaving(false); }
  }
  async function del() {
    if (!window.confirm('¿Eliminar esta caja?')) return;
    try { await api.del(`/api/tpv/boxes/${box.id}`); onSaved(); } catch (e) { alert(e.message); }
  }
  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="lb-eyebrow">Editar caja</div>
            <div className="lb-h" style={{ fontSize: 20, marginTop: 4 }}>{f.name || 'Sin nombre'}</div>
          </div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label="Nombre"><input className="lb-input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="BOLETERÍA · Puerta" /></Field>
          <Field label="PIN (4 dígitos)"><input className="lb-input" value={f.pin} inputMode="numeric" maxLength={4} onChange={e => setF({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="1111" /></Field>
          <Field label="Menú que vende">
            <select className="lb-select" value={f.menu_id} onChange={e => setF({ ...f, menu_id: e.target.value })}>
              <option value="">— Sin menú —</option>
              {menus.map(m => <option key={m.id} value={m.id}>{m.name}{m.type === 'boleteria' ? ' · Boletería' : m.type === 'guardarropa' ? ' · Guardarropa' : ''}</option>)}
            </select>
          </Field>
          {isBoleteria && <LotPicker lots={boleteriaLots} selected={f.boleteria_lot_ids} onChange={ids => setF({ ...f, boleteria_lot_ids: ids })} />}
          {isGuardarropa && <HangerRange from={f.hanger_from} to={f.hanger_to} onChange={(a, b) => setF({ ...f, hanger_from: a, hanger_to: b })} />}
        </div>
        <div style={{ padding: '18px 28px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className="lb-iconbtn" onClick={del} style={{ width: 'auto', padding: '0 12px', height: 38 }}>Eliminar</button>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={onClose} style={{ marginLeft: 'auto' }}>Cancelar</button>
          <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>;
}


// ── RESUMEN DE VENTA DE UNA CAJA (solo panel de administración) ───────────────
// Acá se ve todo lo que vendió la caja, con los duplicados marcados, y se puede
// reimprimir y anular. La reimpresión vive SOLO acá y nunca en el TPV: si la caja
// pudiera reimprimir, un cajero podría emitir un duplicado y alguien retiraría
// mercadería sin pagar. El administrador reimprime desde la computadora que tiene
// la impresora.
function ResumenVentaModal({ box, event, menus, api, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [voiding, setVoiding] = useState(null);     // venta a anular
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [detalle, setDetalle] = useState(null);     // venta expandida

  const load = () => {
    setErr('');
    api.get(`/api/tpv/sales/box-detail?box_id=${box.id}&event_id=${event.id}`)
      .then(setData)
      .catch(e => setErr(e.message || 'No se pudo cargar'));
  };
  useEffect(load, []); // eslint-disable-line

  const menu = menus.find(m => m.id === box.menu_id) || {};
  // Contexto que necesita el ticket para imprimirse igual que el original.
  const printSession = {
    menuType: menu.type || 'barra',
    eventName: event.name,
    orgName: event.org_name || '',
    heroName: '',
    ticket: {
      logo: menu.ticket_logo || null,
      headerType: menu.ticket_header_type || 'event',
      headerText: menu.ticket_header_text || '',
      footer: menu.ticket_footer || '',
    },
  };

  async function reimprimir(sale) {
    if (sale.voided_at && !window.confirm('Esta venta está ANULADA. ¿Reimprimir igual el comprobante?')) return;
    setBusy(true);
    try {
      // Se registra ANTES de imprimir: si el registro falla, no se imprime. Así no
      // puede quedar una reimpresión sin rastro.
      const r = await api.post(`/api/tpv/sales/${sale.id}/reprint`, {});
      const { printBrowser } = await import('../../tpv/printer');
      printBrowser({ ...sale, reprint: true, reprint_n: r.reprints }, printSession);
      load();
    } catch (e) { alert('No se pudo reimprimir: ' + e.message); } finally { setBusy(false); }
  }

  async function anular() {
    if (!reason.trim()) { alert('El motivo es obligatorio'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/api/tpv/sales/${voiding.id}/void`, { reason });
      const rv = r.revertido || {};
      let msg = 'Venta anulada.';
      if (rv.stock) msg += `\nStock devuelto: ${rv.stock} movimiento(s).`;
      if (rv.mesa) msg += `\nSaldo de mesa devuelto: ${money(rv.mesa)}.`;
      if (rv.tickets) msg += `\nEntradas canceladas: ${rv.tickets}.`;
      if (rv.tickets_ya_usados) msg += `\nATENCION: ${rv.tickets_ya_usados} entrada(s) YA se habian usado (esa gente ya entro): no se cancelaron.`;
      // Ventas viejas: sus entradas no quedaron ligadas a la venta, así que no se
      // pueden cancelar solas. Mejor decirlo que dar por hecho que se revirtió todo.
      if (menu.type === 'boleteria' && !rv.tickets && !rv.tickets_ya_usados && Number(voiding.tickets_emitidos || 0) === 0) {
        msg += '\n\nATENCION: esta venta es anterior a la mejora que vincula las entradas con la venta, asi que sus entradas NO se pudieron cancelar automaticamente. Revisalas a mano en Entradas vendidas.';
      }
      alert(msg);
      setVoiding(null); setReason(''); load();
    } catch (e) { alert('No se pudo anular: ' + e.message); } finally { setBusy(false); }
  }

  const fmtHora = (d) => { try { return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' }); } catch { return '—'; } };

  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 880, padding: 26, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div className="lb-eyebrow" style={{ color: '#EC268F' }}>Resumen de venta</div>
            <div className="lb-h" style={{ fontSize: 20 }}>{box.name}</div>
          </div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>

        {err && <div className="lb-err" style={{ marginTop: 12 }}>{err}</div>}
        {!data && !err && <div style={{ padding: '20px 0', color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>}

        {data && (<>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', margin: '14px 0 6px', fontSize: 13 }}>
            <div><span style={{ color: 'var(--ink-600)' }}>Vendido: </span><b style={{ color: '#fff' }}>{money(data.total_vendido)}</b></div>
            <div><span style={{ color: 'var(--ink-600)' }}>Ventas: </span><b style={{ color: '#fff' }}>{data.ventas}</b></div>
            {data.anuladas > 0 && <div><span style={{ color: 'var(--ink-600)' }}>Anuladas: </span><b style={{ color: 'var(--red-400)' }}>{data.anuladas} · {money(data.monto_anulado)}</b></div>}
            {data.dup_confirmados > 0 && <div><span style={{ color: 'var(--ink-600)' }}>Duplicados: </span><b style={{ color: 'var(--red-400)' }}>{data.dup_confirmados}</b></div>}
          </div>

          {/* LA CUENTA DE LA RENDICIÓN, a la vista. Antes arriba decía "vendido" y lo que
              se le pedía a la caja era otro número: lo anulado deja de pedirse y lo pagado
              con saldo de mesa nunca estuvo en la caja. Si no se ve de dónde sale la
              diferencia, al cerrar sobra plata y nadie sabe por qué. */}
          <div className="lb-card" style={{ padding: '14px 16px', background: 'var(--ink-050)', fontSize: 12.5, lineHeight: 1.7, margin: '4px 0 10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ color: 'var(--ink-700)' }}>Vendido (sin anuladas)</span><b style={{ color: '#fff' }}>{money(data.total_vendido)}</b>
            </div>
            {data.con_saldo > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: 'var(--ink-700)' }}>− Pagado con saldo de mesa <span style={{ color: 'var(--ink-600)' }}>(entró al vender la mesa)</span></span>
                <b style={{ color: 'var(--ink-800)' }}>− {money(data.con_saldo)}</b>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 6, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
              <span style={{ color: '#fff', fontWeight: 700 }}>Tiene que rendir</span>
              <b style={{ color: '#fff', fontFamily: 'var(--font-display)', fontSize: 16 }}>{money(data.a_rendir)}</b>
            </div>
            {(data.efectivo > 0 || data.qr > 0) && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed rgba(255,255,255,0.12)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: 'var(--ink-700)' }}>De eso, en efectivo</span><b style={{ color: '#fff' }}>{money(data.efectivo)}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: 'var(--ink-700)' }}>Por QR{data.recargo_qr > 0 ? ` (incluye ${money(data.recargo_qr)} de recargo)` : ''}</span><b style={{ color: '#fff' }}>{money(data.qr)}</b>
                </div>
                {data.sin_medio > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--ink-600)' }}>
                    <span>Sin medio registrado <span style={{ fontSize: 11 }}>(cobradas con la app vieja)</span></span><span>{money(data.sin_medio)}</span>
                  </div>
                )}
              </div>
            )}
            {data.anuladas > 0 && (
              <div style={{ marginTop: 8, color: '#d4af37' }}>
                Ojo: {money(data.monto_anulado)} en {data.anuladas} venta(s) anulada(s) ya no se le piden a la caja. Si esa plata se cobró igual, te va a sobrar al rendir.
              </div>
            )}
          </div>

          {data.dup_confirmados > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-700)', background: 'rgba(200,16,46,0.08)', border: '1px solid rgba(200,16,46,0.35)', borderRadius: 6, padding: '10px 12px', marginBottom: 10, lineHeight: 1.5 }}>
              <b style={{ color: 'var(--red-400)' }}>Duplicado</b> = la misma venta registrada dos veces, idéntica al milisegundo: es un cobro repetido.
              {' '}Dos ventas iguales con segundos de diferencia ya no se marcan: son normales (el mismo cliente pidiendo en tickets separados para no volver a la caja) y la caja hoy no puede duplicar un cobro.
            </div>
          )}

          <div className="tk-scroll-x" style={{ overflowY: 'auto', flex: 1, minHeight: 120 }}>
            <div className="lb-row lb-th" style={{ gridTemplateColumns: '0.5fr 1.4fr 0.8fr 1fr 1.1fr 210px' }}>
              <span>#</span><span>Hora</span><span>Importe</span><span>Estado</span><span>Detalle</span><span></span>
            </div>
            {data.sales.map(s => {
              const anulada = !!s.voided_at;
              const rojo = s.dup_confirmado && !anulada;
              const open = detalle === s.id;
              return (
                <div key={s.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="lb-row" style={{
                    gridTemplateColumns: '0.5fr 1.4fr 0.8fr 1fr 1.1fr 210px', borderBottom: 0, alignItems: 'center',
                    background: rojo ? 'rgba(200,16,46,0.14)' : 'transparent',
                    opacity: anulada ? 0.55 : 1,
                  }}>
                    <span style={{ fontFamily: 'monospace', color: 'var(--ink-700)', fontSize: 12 }}>{String(s.ticket_number || 0).padStart(3, '0')}</span>
                    <span style={{ fontSize: 12, color: 'var(--ink-800)' }}>{fmtHora(s.created_at)}</span>
                    <span style={{ color: '#fff', fontWeight: 600, textDecoration: anulada ? 'line-through' : 'none' }}>{money(s.total)}</span>
                    <span style={{ fontSize: 11.5 }}>
                      {anulada
                        ? <span style={{ color: 'var(--red-400)', fontWeight: 700 }}>ANULADA</span>
                        : rojo ? <span style={{ color: 'var(--red-400)', fontWeight: 700 }}>DUPLICADO</span>
                        : <span style={{ color: '#2ecc71' }}>OK</span>}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>
                      {(s.items || []).length} ítem(s)
                      {s.payment_method && <> · <b style={{ color: s.payment_method === 'qr' ? '#d4af37' : '#2ecc71' }}>{s.payment_method === 'qr' ? 'QR' : 'EFVO'}</b></>}
                      {s.reprints > 0 && <> · <b style={{ color: '#d4af37' }}>{s.reprints} reimpr.</b></>}
                      {Number(s.mesa_paid) > 0 && <> · mesa {money(s.mesa_paid)}</>}
                    </span>
                    <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setDetalle(open ? null : s.id)}>{open ? 'Ocultar' : 'Ver'}</button>
                      <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={busy} onClick={() => reimprimir(s)}>Reimprimir</button>
                      {!anulada && <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ color: 'var(--red-400)' }} onClick={() => { setVoiding(s); setReason(''); }}>Anular</button>}
                    </span>
                  </div>
                  {open && (
                    <div style={{ padding: '8px 0 14px', fontSize: 12.5, color: 'var(--ink-800)' }}>
                      {(s.items || []).map((it, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', maxWidth: 460 }}>
                          <span>{it.qty} × {it.name}</span><span>{money((it.price || 0) * (it.qty || 0))}</span>
                        </div>
                      ))}
                      {s.note && <div style={{ marginTop: 6, color: 'var(--ink-600)' }}>Nota: {s.note}</div>}
                      {Number(s.tickets_emitidos) > 0 && <div style={{ marginTop: 6, color: 'var(--ink-600)' }}>Entradas emitidas: {s.tickets_emitidos}</div>}
                      {anulada && (
                        <div style={{ marginTop: 8, color: 'var(--red-300)', lineHeight: 1.5 }}>
                          Anulada {fmtHora(s.voided_at)}{s.voided_by_name ? ` por ${s.voided_by_name}` : ''}<br />
                          Motivo: <b>{s.void_reason || '—'}</b>
                        </div>
                      )}
                      {s.reprints > 0 && <div style={{ marginTop: 6, color: '#d4af37' }}>Reimpresa {s.reprints} vez/veces · última {fmtHora(s.last_reprint)}</div>}
                    </div>
                  )}
                </div>
              );
            })}
            {data.sales.length === 0 && <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--ink-600)' }}>Esta caja todavía no vendió nada.</div>}
          </div>

          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ink-600)', lineHeight: 1.5 }}>
            Las ventas anuladas quedan registradas (no se borran) y no cuentan en el balance. Cada reimpresión queda asentada y el ticket sale marcado como reimpresión.
          </div>
        </>)}

        {/* Confirmación de anulación: el motivo es obligatorio */}
        {voiding && (
          <div className="lb-modal-backdrop" onClick={() => setVoiding(null)} style={{ zIndex: 400 }}>
            <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460, padding: 24 }}>
              <div className="lb-h" style={{ fontSize: 17, color: 'var(--red-400)', marginBottom: 8 }}>Anular venta de {money(voiding.total)}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.55 }}>
                La venta <b style={{ color: '#fff' }}>no se borra</b>: queda registrada como anulada, con tu nombre y la hora. Se devuelve el stock, el saldo de mesa si había, y se cancelan las entradas que emitió.
              </div>
              <div className="lb-label" style={{ margin: '14px 0 4px' }}>Motivo (obligatorio)</div>
              <input className="lb-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Ej: cobro duplicado por error de red" autoFocus />
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <button className="lb-btn lb-btn--ghost lb-btn--block" onClick={() => setVoiding(null)}>Cancelar</button>
                <button className="lb-btn lb-btn--primary lb-btn--block" style={{ background: reason.trim() ? 'var(--red-600)' : undefined }} disabled={!reason.trim() || busy} onClick={anular}>{busy ? 'Anulando…' : 'Anular venta'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
